import { useEffect, useRef, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { markIntentionalReload } from "@/lib/crashReport";

// Full-screen branded overlay shown when the origin/CDN is momentarily
// unreachable (e.g. Cloudflare 520/502/503/504 during a redeploy). It replaces
// the raw Cloudflare error page with a friendly "Yalola is updating" screen and
// auto-dismisses the moment the backend answers again. Purely visual and
// additive: driven by window events dispatched from lib/api.js.
export const ServiceUpdatingOverlay = () => {
  const { t } = useApp();
  const [visible, setVisible] = useState(false);
  const pollRef = useRef(null);
  const confirmingRef = useRef(false);

  useEffect(() => {
    // A single 502/503/504 on ONE request is almost always a transient blip
    // (proxy timeout on a heavy endpoint, a pod recycling, a flaky poll) — NOT a
    // real outage. Blanking the whole app on the first blip is what made this
    // screen appear far too often. So we CONFIRM first: only after two quick
    // health pings both fail do we actually show the "updating" overlay.
    const confirmDown = async () => {
      if (confirmingRef.current || visible) return;
      confirmingRef.current = true;
      let downStreak = 0;
      for (let i = 0; i < 2; i++) {
        try {
          await api.get("/config/public", { timeout: 6000 });
          confirmingRef.current = false; // backend answered → it was just a blip
          return;
        } catch {
          downStreak += 1;
        }
        if (i === 0) await new Promise((r) => setTimeout(r, 1200));
      }
      confirmingRef.current = false;
      if (downStreak >= 2) setVisible(true);
    };
    const onDown = () => { confirmDown(); };
    const onOk = () => { confirmingRef.current = false; setVisible(false); };
    window.addEventListener("yalola:server-unavailable", onDown);
    window.addEventListener("yalola:server-ok", onOk);
    return () => {
      window.removeEventListener("yalola:server-unavailable", onDown);
      window.removeEventListener("yalola:server-ok", onOk);
    };
  }, [visible]);

  // While shown, ping a lightweight public endpoint until the backend recovers.
  useEffect(() => {
    if (!visible) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    const check = async () => {
      try { await api.get("/config/public", { timeout: 8000 }); setVisible(false); }
      catch { /* still down — keep polling */ }
    };
    const t = setTimeout(check, 1500);
    pollRef.current = setInterval(check, 4000);
    return () => { clearTimeout(t); if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [visible]);

  if (!visible) return null;
  return (
    <div
      data-testid="service-updating-overlay"
      className="fixed inset-0 z-[100001] grid place-items-center px-8 animate-in fade-in"
      style={{ background: "var(--yalola-bg)" }}
    >
      <div className="flex flex-col items-center text-center max-w-sm">
        <div className="h-16 w-16 rounded-2xl grid place-items-center shadow-lg mb-6" style={{ background: "var(--yalola-primary)" }}>
          <img src="/yalola-logo.png" alt="Yalola" className="h-9 w-9 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        </div>
        <div
          className="h-8 w-8 mb-5 rounded-full border-[3px] animate-spin"
          style={{ borderColor: "var(--yalola-primary)", borderTopColor: "transparent" }}
          aria-hidden
        />
        <h2 className="text-lg font-bold" style={{ color: "var(--yalola-text)" }}>{t("update.title", "Yalola se met à jour…")}</h2>
        <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--yalola-text-muted)" }}>
          {t("update.body", "Un petit instant, on améliore ton expérience. La page se rétablit toute seule dans quelques secondes.")}
        </p>
        <button
          onClick={() => { markIntentionalReload("retry"); window.location.reload(); }}
          className="mt-6 px-6 py-2.5 rounded-full text-sm font-semibold text-white shadow-md transition-transform active:scale-95"
          style={{ background: "var(--yalola-primary)" }}
          data-testid="service-updating-retry"
        >
          {t("update.retry", "Réessayer maintenant")}
        </button>
      </div>
    </div>
  );
};

export default ServiceUpdatingOverlay;
